import React, { useState } from "react"
import { ButtonLoadingSeeker, IconSeeker } from "@ui-seeker/core"
import { Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, List, ListItem, ListItemText, Typography, Chip, Divider } from "@mui/material"
import {useUploadCsv} from "./@hooks";

function formatErrorData(data) {
    if (!data) return null
    if (typeof data === 'string') return data
    if (typeof data === 'object') {
        const relevantKeys = ['MODEL', 'CATEGORY', 'ACRISS', 'OFFICE COUNTRY', 'OFFICE NAME', 'PROVIDERS', 'WEB', 'DOORS', 'SEATS']
        return Object.entries(data)
            .filter(([key]) => relevantKeys.includes(key.toUpperCase()))
            .map(([key, value]) => `${key}: ${value}`)
            .join(', ')
    }
    return String(data)
}

function formatErrorMessage(error) {
    if (!error) return ''
    if (error.includes('validation error')) {
        const match = error.match(/Input should be ([^[]+)/g)
        if (match) return match.join('; ')
    }
    return error
}

function ValidationPreviewDialog({ open, onClose, onConfirm, result, isLoading }) {
    if (!result) return null

    const { valid_items = [], valid_count = 0, update_items = [], update_count = 0, failed = 0, total_rows = 0, errors = [], skipped = [], skipped_count = 0, warnings = [], warning_count = 0 } = result
    const hasValidItems = valid_count > 0 || update_count > 0

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>XLSX Validation Preview</DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`Total rows: ${total_rows}`} color="default" />
                    {valid_count > 0 && <Chip label={`New: ${valid_count}`} color="success" />}
                    {update_count > 0 && <Chip label={`Updates: ${update_count}`} color="info" />}
                    {skipped_count > 0 && <Chip label={`Skipped: ${skipped_count}`} color="warning" />}
                    {warning_count > 0 && <Chip label={`Warnings: ${warning_count}`} color="warning" variant="outlined" />}
                    {failed > 0 && <Chip label={`Errors: ${failed}`} color="error" />}
                </Box>

                {hasValidItems && failed === 0 && skipped_count === 0 && warning_count === 0 && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {valid_count > 0 && update_count > 0
                            ? `${valid_count} new rows and ${update_count} updates are ready to be applied.`
                            : valid_count > 0
                                ? `All ${valid_count} rows are new and ready to be applied.`
                                : `${update_count} existing records will be updated.`
                        }
                    </Alert>
                )}

                {hasValidItems && (failed > 0 || skipped_count > 0 || warning_count > 0) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {valid_count > 0 && `${valid_count} new rows`}
                        {valid_count > 0 && update_count > 0 && ' and '}
                        {update_count > 0 && `${update_count} updates`}
                        {' can be applied. '}
                        {failed > 0 && `${failed} rows have errors. `}
                        {skipped_count > 0 && `${skipped_count} duplicate rows will be skipped. `}
                        {warning_count > 0 && `${warning_count} rows have ignored values (not found).`}
                    </Alert>
                )}

                {!hasValidItems && failed > 0 && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        No valid rows found. All {failed} rows have errors.
                    </Alert>
                )}

                {valid_items.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }} color="success.main">
                            New items to be added ({valid_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.200' }}>
                            {valid_items.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={<Typography variant="body2">{item.display}</Typography>}
                                    />
                                </ListItem>
                            ))}
                            {valid_items.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography variant="body2" color="text.secondary">... and {valid_items.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {update_items.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }} color="info.main">
                            Existing items to be updated ({update_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'info.50', borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
                            {update_items.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={<Typography variant="body2">{item.display}</Typography>}
                                        secondary={
                                            <Typography variant="caption" color="text.secondary">
                                                {item.old_value} → {item.new_value}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {update_items.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography variant="body2" color="text.secondary">... and {update_items.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {skipped.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }} color="warning.main">
                            Skipped ({skipped_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                            {skipped.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {item.reason === 'duplicate' ? (
                                                    <>
                                                        <Chip size="small" label={`Row ${item.row}`} color="warning" variant="outlined" />
                                                        <Typography variant="body2">
                                                            Duplicate of row {item.original_row}
                                                        </Typography>
                                                    </>
                                                ) : item.reason === 'no_change' ? (
                                                    <>
                                                        <Chip size="small" label="No change" color="default" variant="outlined" />
                                                        <Typography variant="body2">
                                                            Already exists with same value: {item.value}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <Typography variant="body2">{item.reason}</Typography>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                {item.data}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {skipped.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography variant="body2" color="text.secondary">... and {skipped.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {warnings.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }} color="warning.main">
                            Warnings ({warning_count}):
                        </Typography>
                        <List dense sx={{ maxHeight: 150, overflow: 'auto', bgcolor: 'warning.50', borderRadius: 1, border: '1px solid', borderColor: 'warning.200' }}>
                            {warnings.slice(0, 20).map((item, idx) => (
                                <ListItem key={idx} dense>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                <Chip size="small" label={item.field} color="warning" variant="outlined" />
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                    {item.message}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                {item.data}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                            ))}
                            {warnings.length > 20 && (
                                <ListItem>
                                    <ListItemText primary={<Typography variant="body2" color="text.secondary">... and {warnings.length - 20} more</Typography>} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}

                {errors.length > 0 && (
                    <Box>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle2" sx={{ mb: 1 }} color="error">
                            Errors ({errors.length}):
                        </Typography>
                        <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.100', borderRadius: 1 }}>
                            {errors.slice(0, 30).map((err, idx) => (
                                <ListItem key={idx} divider>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                {err.row && <Chip size="small" label={`Row ${err.row}`} color="default" variant="outlined" />}
                                                <Chip size="small" label={err.field} color="error" variant="outlined" />
                                                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                                    {formatErrorMessage(err.error)}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            formatErrorData(err.data) && (
                                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                                    {formatErrorData(err.data)}
                                                </Typography>
                                            )
                                        }
                                    />
                                </ListItem>
                            ))}
                            {errors.length > 30 && (
                                <ListItem>
                                    <ListItemText primary={`... and ${errors.length - 30} more errors`} />
                                </ListItem>
                            )}
                        </List>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                {hasValidItems && (
                    <Button
                        onClick={onConfirm}
                        variant="contained"
                        color="primary"
                        disabled={isLoading}
                    >
                        {isLoading ? "Applying..." : `Apply ${valid_count + update_count} items`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default function UploadXlsButton({ path }) {
    const { fileRef, validateFile, applyChanges, isLoadingValidate, isLoadingApply, validationResult, clearResult } = useUploadCsv(path)
    const [dialogOpen, setDialogOpen] = useState(false)

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            validateFile(file)
        }
    }

    React.useEffect(() => {
        if (validationResult.value) {
            setDialogOpen(true)
        }
    }, [validationResult.value])

    const handleConfirm = () => {
        applyChanges()
        setDialogOpen(false)
    }

    const handleClose = () => {
        setDialogOpen(false)
        clearResult()
        if (fileRef.current) {
            fileRef.current.value = ''
        }
    }

    const isLoading = isLoadingValidate.value || isLoadingApply.value

    return (
        <>
            <ButtonLoadingSeeker
                sx={{ ml: 2 }}
                endIcon={<IconSeeker icon="file-xls"/>}
                loading={isLoading}
                component="label"
            >
                <Box display={'none'}>
                    <input
                        type="file"
                        ref={fileRef}
                        accept=".xlsx"
                        onChange={handleFileChange}
                    />
                </Box>
                {isLoadingValidate.value ? "Validating..." : isLoadingApply.value ? "Applying..." : "Upload XLSX"}
            </ButtonLoadingSeeker>

            <ValidationPreviewDialog
                open={dialogOpen}
                onClose={handleClose}
                onConfirm={handleConfirm}
                result={validationResult.value}
                isLoading={isLoadingApply.value}
            />
        </>
    )
}